import { Link } from 'react-router'
import { RiArrowRightLine, RiCheckLine, RiStarLine } from 'react-icons/ri'
import { ROUTES } from '@/constants/routes'
import { useInView } from '@/hooks'

export const ServicesJourneySection = () => {
  const { ref, inView } = useInView<HTMLDivElement>()

  return (
    <section className="bg-crimson-50 py-20 lg:py-28 overflow-hidden relative border-t border-crimson-100">
      {/* Decorative circles */}
      <div
        aria-hidden="true"
        className="absolute top-0 right-0 w-96 h-96 rounded-full bg-white opacity-50 -translate-y-1/3 translate-x-1/3 pointer-events-none"
      />
      <div
        aria-hidden="true"
        className="absolute bottom-0 left-0 w-72 h-72 rounded-full bg-gold-50 opacity-60 translate-y-1/3 -translate-x-1/4 pointer-events-none"
      />
      <div
        aria-hidden="true"
        className="absolute right-0 top-0 font-display font-bold text-crimson-200 opacity-40 select-none pointer-events-none leading-none hidden xl:block"
        style={{ fontSize: '22rem', letterSpacing: '-0.04em' }}
      >
        04
      </div>

      <div ref={ref} className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className={`mb-12 ${inView ? 'animate-fade-in-up' : 'opacity-0'}`}>
          <div className="flex items-center gap-3 mb-5">
            <span className="inline-flex items-center gap-1.5 bg-gold-500 text-base-900 text-[10px] font-bold uppercase tracking-[0.22em] px-4 py-2 rounded-full shadow-sm">
              <RiStarLine size={11} />
              Complete Package — Best Value
            </span>
          </div>
          <h2
            className="font-display font-bold text-base-900 leading-tight mb-4"
            style={{ fontSize: 'clamp(2rem, 4.5vw, 3.25rem)' }}
          >
            Why Choose the Full{' '}
            <em className="text-crimson-600 not-italic italic">H.E.R Birth Journey</em> Experience?
          </h2>
          <div className="w-14 h-[3px] rounded-full bg-gradient-to-r from-crimson-600 to-gold-500" />
        </div>

        <div className="flex flex-col lg:flex-row gap-12 lg:gap-20">
          {/* Left: description */}
          <div
            className={`flex-1 space-y-5 text-[15px] text-base-600 leading-[1.9] ${inView ? 'animate-fade-in-up' : 'opacity-0'}`}
            style={{ animationDelay: inView ? '0.15s' : undefined }}
          >
            <p>
              Choosing the full H.E.R Birth Journey Experience means choosing continuous, consistent
              support — rather than navigating each stage of your journey alone. From pregnancy
              through to postpartum, your care is connected.
            </p>
            <p>
              You are not starting over with someone new at each stage, but instead building trust,
              safety, and understanding with someone who already knows you, your preferences, and
              your story. This continuity allows for deeper emotional support, more personalised
              guidance, and a stronger sense of being held throughout your entire experience.
            </p>
            <p>
              If you are looking for a space where you feel seen, supported, and empowered from
              beginning to beyond birth, this experience has been created for you.
            </p>
          </div>

          {/* Right: includes + pricing card */}
          <div
            className={`lg:w-[380px] shrink-0 ${inView ? 'animate-fade-in-up' : 'opacity-0'}`}
            style={{ animationDelay: inView ? '0.2s' : undefined }}
          >
            <div className="bg-white rounded-3xl p-7 lg:p-8 shadow-[0_32px_64px_rgba(0,0,0,0.3)]">
              <div className="w-10 h-[3px] rounded-full bg-gradient-to-r from-gold-500 to-gold-300 mb-6" />

              <p className="text-[10.5px] font-bold uppercase tracking-[0.2em] text-base-400 mb-4">
                Everything Included
              </p>

              <ul className="space-y-3 mb-6">
                {[
                  'Full H.E.R Antenatal Empowerment Experience',
                  'H.E.R Birth Healing Debrief Sessions (3 × 1 hour)',
                  'H.E.R Restoration Postpartum Home Visits (3 × 3 hours)',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-crimson-50 flex items-center justify-center shrink-0">
                      <RiCheckLine size={11} className="text-crimson-600" />
                    </span>
                    <span className="text-[13.5px] text-base-700 leading-snug">{item}</span>
                  </li>
                ))}
              </ul>

              <div className="border-t border-base-100 pt-5 mb-5">
                <div className="flex items-baseline gap-3 mb-1">
                  <span
                    className="font-display font-bold text-gold-500 leading-none"
                    style={{ fontSize: '2.75rem' }}
                  >
                    £1,250
                  </span>
                  <span className="text-base-400 text-sm line-through">£1,385</span>
                </div>
                <span className="inline-flex items-center bg-gold-50 text-gold-600 text-[11px] font-bold uppercase tracking-wide px-3 py-1 rounded-full border border-gold-200">
                  Save £135
                </span>
              </div>

              <Link to={ROUTES.ENQUIRY} className="btn-primary w-full justify-center">
                Begin Your Journey
                <RiArrowRightLine size={17} />
              </Link>

              <p className="text-[11px] text-base-400 text-center mt-3">
                Book a free call first — no obligation
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
